// Performance monitoring: Track when JavaScript execution starts
const appStartTime = performance.now();
console.log( '[PERF] JavaScript execution started', { time: new Date().toISOString() });

import { ProvideAuth } from '@contexts/useAuth/useAuth';
import { ProvideBackend } from '@contexts/useBackend/useBackend';
import { ProvideError } from '@contexts/useError/useError';
import { ProvideLoadingBar } from '@contexts/useLoadingBar/useLoadingBar';
import { ProvideUserPreferences } from '@contexts/useUserPreferences/useUserPreferences';

import '@mantine/carousel/styles.css';
import '@mantine/charts/styles.css';
import { MantineProvider } from '@mantine/core';
import type { CSSVariablesResolver } from '@mantine/core';
import { PostHogProvider } from 'posthog-js/react';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/dropzone/styles.css';
import '@mantine/notifications/styles.css';

import { store } from '@store/store';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import App from './App';
import reportWebVitals from './reportWebVitals';

import './styles/global.css';
import { theme } from './styles/theme';
import './styles/variables.css';
import './translation/i18n';
import 'dayjs/locale/es';
import 'dayjs/locale/en';

import { TourProvider } from '@contexts/useTour/useTour';
import { ErrorBoundary } from './ErrorBoundary';
import { ProvideIntegrations } from '@contexts/useIntegrations/useIntegrations';
import { ProvideFeatureFlags, useFeatureFlagProvider } from './infrastructure/featureFlags/ProvideFeatureFlags';
import { ProvideAuthMemberships } from '@contexts/useAuthMemberships/useAuthMemberships';

// Performance monitoring: Track import completion time
console.log( '[PERF] All imports completed', { duration: `${( performance.now() - appStartTime ).toFixed( 0 )}ms` });

const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const isPosthogEnabled = import.meta.env.VITE_POSTHOG_ENABLED !== 'false' && posthogKey !== undefined;

const postHogOptions = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST
};

document.documentElement.dataset.stage = import.meta.env.VITE_EXCECUTION_ENVIRONMENT;
const root = ReactDOM.createRoot(
  document.getElementById( 'root' ) as HTMLElement
);

/**
 * Inner component that wires ProvideAuthMemberships' onMembershipChange callback
 * to the feature flag provider's refetchFlags method.
 * Must be rendered inside ProvideFeatureFlags to access the provider context.
 */
function AuthMembershipsWithFlagRefetch({ children }: { children: React.ReactNode }) {
  const { provider } = useFeatureFlagProvider();
  return (
    <ProvideAuthMemberships onMembershipChange={() => provider?.refetchFlags()}>
      {children}
    </ProvideAuthMemberships>
  );
}

const AppTree = (
  <ProvideLoadingBar>
    <ProvideBackend>
      <ProvideAuth>
        <ProvideFeatureFlags>
          <AuthMembershipsWithFlagRefetch>
            <ProvideIntegrations>
              <ProvideUserPreferences>
                <Provider store={store}>
                  <TourProvider>
                    <App />
                  </TourProvider>
                </Provider>
              </ProvideUserPreferences>
            </ProvideIntegrations>
          </AuthMembershipsWithFlagRefetch>
        </ProvideFeatureFlags>
      </ProvideAuth>
    </ProvideBackend>
  </ProvideLoadingBar>
);

// Hide the static HTML loading screen now that React is mounting
const staticLoading = document.getElementById( 'static-loading' );
if ( staticLoading ) {
  staticLoading.style.display = 'none';
}

/**
 * Publishes `theme.other` tokens as CSS variables so a CSS module can read them
 * without re-typing the value.
 *
 * `theme.ts` is the source of truth for design tokens (design-system.md says so
 * outright), but a CSS module cannot import TypeScript. Without this bridge the
 * only way to use a brand token from CSS is to paste the hex, and then there
 * are two copies that drift the first time one of them is edited — which is
 * exactly what happened with the setup-checklist header before this existed.
 *
 * Same value in both arms: `headerDeep` is a fixed brand colour, not a
 * light/dark pair.
 */
const cssVariablesResolver: CSSVariablesResolver = ( t ) => ({
  variables: {
    '--teselly-header-deep': t.other.brand.headerDeep
  },
  light: {},
  dark: {}
});

root.render(
  <MantineProvider
    theme={theme}
    defaultColorScheme="light"
    cssVariablesResolver={cssVariablesResolver}
  >
    <ProvideError>
      <ErrorBoundary>
        {isPosthogEnabled ? (
          <PostHogProvider apiKey={posthogKey!} options={postHogOptions}>
            {AppTree}
          </PostHogProvider>
        ) : (
          AppTree
        )}
      </ErrorBoundary>
    </ProvideError>
  </MantineProvider>
);

reportWebVitals( console.log );